/**
 * The chat's data layer.
 *
 * Threads and messages go through ts-rest like everything else. Asking a
 * question does not — it is an event stream, and lives in `use-chat-stream.ts`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ChatMessage, ChatThread, Decision, QuestionSummary } from "@gb/contracts";
import { api, errorMessage } from "@/api/client";
import { awaitingAnswer, stillAnswering } from "./answer-wait";

/** How often an unanswered question re-checks. The tab is open in front of
 *  somebody waiting. */
const POLL_WHILE_ANSWERING_MS = 2_000;

/** How often the history re-checks while a question on its page is still being answered. */
const POLL_HISTORY_MS = 5_000;

export const chatKeys = {
  all: (projectId: string) => ["project", projectId, "chat"] as const,
  /** Every thread list for this project. For invalidation. */
  allThreads: (projectId: string) => ["project", projectId, "chat", "threads"] as const,
  thread: (projectId: string, threadId: string) =>
    ["project", projectId, "chat", "thread", threadId] as const,
  /**
   * The limit and the offset are part of the key.
   *
   * Without them, two callers asking for different amounts shared one cache
   * entry and whichever query ran first won — so a screen wanting thirty rows
   * could be handed the one row another screen had asked for. A key that omits
   * a parameter the response depends on is a key that lies.
   */
  questions: (projectId: string, limit: number, offset: number) =>
    ["project", projectId, "chat", "questions", { limit, offset }] as const,
  /** Every question list for this project, whatever its limit. For invalidation. */
  allQuestions: (projectId: string) => ["project", projectId, "chat", "questions"] as const,
};

/**
 * Accept an answer as a decision.
 *
 * The decision text is **not** sent — it is written server-side from the
 * question, the answer, its sources and the note. All this carries is the
 * person's own note about what they changed, when they used *Modify & accept*.
 */
export function useElevateToDecision(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      questionId: string;
      modificationNote?: string;
    }): Promise<Decision> => {
      const res = await api.chat.elevateToDecision({
        params: { id: projectId, questionId: input.questionId },
        body: input.modificationNote ? { modificationNote: input.modificationNote } : {},
      });
      if (res.status !== 201) throw new Error(errorMessage(res));
      return res.body.decision;
    },
    // `onSettled`, not `onSuccess`, and the question's own record is the one
    // that matters. **Both halves were bugs.**
    //
    // `decisionId` lives on the question's response — the history list is paged
    // and cannot be relied on to hold the record being viewed — so invalidating
    // only the list left the panel still offering `Accept decision` on
    // something it had just filed. Pressing it again earned an honest 409 and
    // an error message, which is the server being right and the screen being
    // stale.
    //
    // And on failure: a 409 means *somebody already elevated this*, which is
    // exactly the case where the screen most needs to go and re-read. Settling
    // rather than succeeding is what turns that error into the truth.
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: chatKeys.thread(projectId, variables.questionId),
      });
      void queryClient.invalidateQueries({ queryKey: chatKeys.allQuestions(projectId) });
      // The decision is a `knowledge_base` row now, so a stale Brain list would
      // be missing the thing the user just filed.
      void queryClient.invalidateQueries({ queryKey: ["project", projectId, "brain"] });
    },
  });
}

/**
 * One page of the question history, newest first.
 *
 * `total` comes back with the page because a list that simply stops at the page
 * size cannot be told from one that has run out — the panel needs to know
 * whether another page exists before it can offer to turn it.
 *
 * `placeholderData` keeps the previous page on screen while the next loads, so
 * paging does not collapse the panel to nothing and shove everything above it
 * up the page for a beat.
 */
export function useQuestions(
  projectId: string | undefined,
  page: { limit?: number; offset?: number } = {},
) {
  const limit = page.limit ?? 10;
  const offset = page.offset ?? 0;
  return useQuery({
    queryKey: chatKeys.questions(projectId ?? "", limit, offset),
    enabled: Boolean(projectId),
    placeholderData: (previous) => previous,
    // Read again while a question on the page is still being answered
    // (2026-09-27): its row turns into its answer, or into "No answer" when
    // the wait runs out.
    refetchInterval: (query) =>
      query.state.data?.questions.some((question) => stillAnswering(question)) ? POLL_HISTORY_MS : false,
    queryFn: async (): Promise<{ questions: QuestionSummary[]; total: number }> => {
      const res = await api.chat.listQuestions({
        params: { id: projectId! },
        query: { limit, offset },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return { questions: res.body.questions, total: res.body.total };
    },
  });
}

export function useChatThread(projectId: string | undefined, threadId: string | undefined) {
  return useQuery({
    queryKey: chatKeys.thread(projectId ?? "", threadId ?? ""),
    enabled: Boolean(projectId && threadId),
    // **Poll while it has no answer.** A question is a row the moment it is
    // asked, so it can be opened before anything has been written — click it
    // from the history mid-answer, or reload while it is streaming and this
    // tab's buffer is gone while the server carries on. Without this the panel
    // sits empty until somebody navigates, which is indistinguishable from a
    // hang. Same shape as `documents-api.ts` polling a parse.
    //
    // **For 25 minutes at most** (2026-09-27): past that, no answer is coming
    // (`answerDeadline` in `answer-wait.ts`).
    refetchInterval: (query) => (awaitingAnswer(query.state.data) ? POLL_WHILE_ANSWERING_MS : false),
    queryFn: async (): Promise<{
      thread: ChatThread;
      messages: ChatMessage[];
      decisionId: string | null;
    }> => {
      const res = await api.chat.getThread({
        params: { id: projectId!, threadId: threadId! },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

export function useCreateThread(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (question?: string): Promise<ChatThread> => {
      const res = await api.chat.createThread({
        params: { id: projectId },
        body: question ? { question } : {},
      });
      if (res.status !== 201) throw new Error(errorMessage(res));
      return res.body.thread;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.allThreads(projectId) });
    },
  });
}
